import * as React from 'react'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '~/sotano-kit/primitives/tooltip'
import { BaseHubAvatar } from '~/components/basehub-avatar'
import { useUserData } from '~/hooks-data/use-user-data'
import { makeFullNameFromClerkUser } from '@basehub/data/auth/utils/make-full-name-from-clerk-user'
import { Skeleton } from '~/sotano-kit/primitives/skeleton'
import { Client } from '@basehub/data/replicache/index'
import { clsx } from 'clsx'
import { TooltipPortal } from '@radix-ui/react-tooltip'
import { SYSTEM_AUTHORS } from '@basehub/shared-constants'

interface Props extends React.HTMLAttributes<HTMLDivElement> {
  userId: string
  activeClient: Client | null
  collisionBoundary?: HTMLElement | HTMLElement[] | null
  borderSize?: number
}

export const MainContribuitor: React.FC<Props> = ({
  userId,
  style,
  activeClient,
  className,
  borderSize = 1,
  collisionBoundary,
  ...props
}) => {
  const userQuery = useUserData(userId)
  const user = userQuery.data

  let fullName = activeClient ? 'Loading active user...' : 'Loading...'

  if (user) {
    if (user.id === SYSTEM_AUTHORS.UNKNOWN.id && activeClient) {
      fullName = 'Playground user'
    } else {
      fullName = makeFullNameFromClerkUser(user)
    }
  }

  return (
    <Tooltip delayDuration={100}>
      <TooltipTrigger asChild>
        <div>
          {activeClient ? (
            <div
              className={clsx(
                'relative transition-all duration-200 ease-in-out',
                className
              )}
              style={{
                border: `${borderSize}px solid ${activeClient.color}`,
                borderRadius: '50%',
                ...style,
              }}
            >
              <BaseHubAvatar
                size="small"
                style={{
                  // overrides min-width from small size
                  minWidth: 24 - borderSize * 2,
                }}
                id={activeClient.userId}
                name={fullName}
                className="object-cover max-h-full max-w-full rounded-full"
              />
            </div>
          ) : (
            <>
              {user ? (
                <div
                  className={clsx(
                    'relative transition-all duration-200 ease-in-out',
                    className
                  )}
                  style={style}
                  {...props}
                >
                  <BaseHubAvatar
                    className="relative rounded-full border border-dark-border-solid transition-all duration-200 ease-out"
                    name={fullName}
                    id={user.id}
                    size="small"
                    style={{
                      width: style?.width,
                      height: style?.height,
                      borderWidth: borderSize,
                      minWidth: style?.width,
                    }}
                    forceSrc={
                      userQuery.data?.isMe
                        ? (userQuery.data.imageUrl ?? undefined)
                        : undefined
                    }
                  />
                </div>
              ) : (
                <div
                  className={clsx(
                    'relative transition-all duration-200 ease-in-out',
                    className
                  )}
                  style={style}
                  {...props}
                >
                  <Skeleton
                    style={{
                      borderWidth: borderSize,
                      borderStyle: 'solid',
                    }}
                    className="h-6 w-6 !rounded-full border-dark-border-solid"
                    width={style?.width ?? 24}
                    height={style?.height ?? 24}
                  />
                </div>
              )}
            </>
          )}
        </div>
      </TooltipTrigger>
      <TooltipPortal>
        <TooltipContent
          collisionBoundary={collisionBoundary}
          className="py-1 !px-2"
        >
          {fullName}
          {Boolean(activeClient && user) ? (
            <span className="text-dark-faint"> is active</span>
          ) : (
            ''
          )}
        </TooltipContent>
      </TooltipPortal>
    </Tooltip>
  )
}
